import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import PageShell from "../components/layout/PageShell";
import ProjectAccountAssignCard from "../components/crm/ProjectAccountAssignCard";
import ProjectDomainsPanel from "../components/crm/ProjectDomainsPanel";
import ProjectOperationsModal from "../components/crm/ProjectOperationsModal";
import {
    getCrmProjectDetail,
    getModules,
    publishCrmProject,
    getAvailableSeeds,
    regenerateFromSeed,
    redeployProject,
    fixWebsite,
    applyWebsiteSectionFix,
    previewWebsiteSectionFix,
    getVariantStats,
    regenerateChallenger,
    resetVariantStats,
    getDeploymentStatus,
    getDeploymentVersions,
    rollbackDeploymentVersion,
    getProductionFit,
    analyzeProductionFit,
    getPremiumUpgradePreview,
    generatePremiumUpgradePreview,
    applyPremiumUpgrade,
    getAccounts,
    fixProductionFitIssues,
    getProjectHealth,
    repairCrmFromSeed,
    getProjectLeadSummary
} from "../services/crmService";
import {
    isSuperAdminUser,
    canUseProjectDiagnostics,
    canPreviewProjectUpgrade,
    canApplyProjectUpgrade,
    canUseProjectAiStudio,
    canRepairCrmFromSeed,
    canAssignProjectAccounts
} from "../services/authService";

import "./project.css";

function sanitizeErrorMessage(value, fallback = "Something went wrong.") {
    const text = String(value || "").trim();

    if (!text) return fallback;

    const lower = text.toLowerCase();

    const technicalMarkers = [
        "system.",
        "exception",
        "stacktrace",
        " at ",
        "/home/runner/",
        "c:\\",
        "jsonserializer",
        "jsonexception",
        "jsonreaderexception",
        "npgsql",
        "microsoft.entityframeworkcore",
        "shrianshnexus.infrastructure",
        "shrianshnexus.api"
    ];

    if (technicalMarkers.some((marker) => lower.includes(marker))) {
        if (
            lower.includes("airesponsejson") ||
            lower.includes("jsonexception") ||
            lower.includes("jsonreaderexception") ||
            lower.includes("deserialize")
        ) {
            return "Generated website content is incomplete or corrupted. Please run Repair Content or regenerate this website before publishing.";
        }

        return "Something went wrong while processing this request. Please retry or contact support if the issue continues.";
    }

    return text.length > 300 ? `${text.slice(0, 300)}...` : text;
}

function getErrorMessage(err, fallback = "Something went wrong.") {
    const apiError = err?.response?.data;

    if (typeof apiError === "string") {
        return sanitizeErrorMessage(apiError, fallback);
    }

    if (apiError?.message) {
        return sanitizeErrorMessage(apiError.message, fallback);
    }

    if (apiError?.title) {
        return sanitizeErrorMessage(
            `${apiError.title}${apiError.status ? ` (${apiError.status})` : ""}`,
            fallback
        );
    }

    if (err?.message) {
        return sanitizeErrorMessage(err.message, fallback);
    }

    return fallback;
}

function formatPipelineStatus(value) {
    if (!value) return "-";
    if (value === "superseded") return "superseded by redeploy";
    if (value === "stale") return "stale ignored";
    return value;
}

function formatDateTime(value) {
    if (!value) return "-";

    try {
        return new Date(value).toLocaleString();
    } catch {
        return "-";
    }
}

function getWorkflowStepState(status, step) {
    const current = status?.status || "idle";

    if (current === "failed") {
        if (step === "live") return "failed";
        return "done";
    }

    if (current === "idle") return "idle";

    if (current === "syncing") {
        if (step === "saving") return "done";
        if (step === "syncing") return "active";
        return "idle";
    }

    if (current === "redeploying") {
        if (step === "saving" || step === "syncing") return "done";
        if (step === "redeploying") return "active";
        return "idle";
    }

    if (current === "synced") {
        if (step === "saving" || step === "syncing") return "done";
        return "idle";
    }

    if (current === "live") return "done";

    return "idle";
}

function InfoPill({ label, value }) {
    return (
        <div className="project-pill">
            <span>{label}</span>
            <strong>{value || "-"}</strong>
        </div>
    );
}

function StatCard({ label, value, icon }) {
    return (
        <div className="project-stat-card">
            <div className="project-stat-icon">{icon}</div>
            <span>{label}</span>
            <strong>{value || 0}</strong>
        </div>
    );
}

function ActionButton({ children, onClick, disabled, variant = "secondary" }) {
    return (
        <button
            type="button"
            className={`project-action-btn ${variant}`}
            disabled={disabled}
            onClick={onClick}
        >
            {children}
        </button>
    );
}

function OperationNote({ children }) {
    return (
        <p className="operation-note">
            {children}
        </p>
    );
}

function ProjectHealthPanel({
    health,
    onRefresh,
    onOpenSeedPicker,
    onRepairCrm,
    onRedeploy,
    repairingCrm = false,
    redeploying = false,
    isSuperAdmin = false
}) {
    if (!health) {
        return (
            <section className="project-panel project-health-panel">
                <div className="project-panel-header">
                    <div>
                        <span className="project-kicker">Health</span>
                        <h2>Project Health</h2>
                        <p>Health data is not loaded yet.</p>
                    </div>
                    <button
                        type="button"
                        className="project-action-btn secondary"
                        onClick={onRefresh}
                    >
                        Refresh
                    </button>
                </div>
            </section>
        );
    }

    const actions = health.recommendedActions || [];
    const emptyModules = health.crm?.emptyModules || [];

    return (
        <section className={`project-panel project-health-panel health-${health.overallStatus || "ok"}`}>
            <div className="project-panel-header">
                <div>
                    <span className="project-kicker">Launch Readiness</span>
                    <h2>Project Health</h2>
                    <p>{health.overallLabel || "Project health summary."}</p>
                </div>

                <div className="project-health-head-actions">
                    <HealthBadge status={health.overallStatus} />
                    <button
                        type="button"
                        className="project-action-btn secondary"
                        onClick={onRefresh}
                    >
                        Refresh
                    </button>
                </div>
            </div>

            <div className="project-health-grid">
                <div className="project-health-card">
                    <span>Generation</span>
                    <strong>{formatHealthStatus(health.generation?.status)}</strong>
                    <p>{health.generation?.message}</p>
                </div>

                <div className="project-health-card">
                    <span>CRM</span>
                    <strong>{formatHealthStatus(health.crm?.status)}</strong>
                    <p>{health.crm?.message}</p>
                </div>

                <div className="project-health-card">
                    <span>Seed</span>
                    <strong>{formatHealthStatus(health.seed?.status)}</strong>
                    <p>{health.seed?.message}</p>
                </div>

                <div className="project-health-card">
                    <span>Deployment</span>
                    <strong>{formatHealthStatus(health.deployment?.status)}</strong>
                    <p>{health.deployment?.message}</p>
                </div>
            </div>

            <div className="project-health-counts">
                <div>
                    <span>Modules</span>
                    <strong>{health.crm?.moduleDefinitionCount || 0}</strong>
                </div>
                <div>
                    <span>Published Items</span>
                    <strong>{health.crm?.publishedItemCount || 0}</strong>
                </div>
                <div>
                    <span>Singletons</span>
                    <strong>{health.crm?.singletonCount || 0}</strong>
                </div>
                <div>
                    <span>Empty Modules</span>
                    <strong>{emptyModules.length}</strong>
                </div>
            </div>

            {emptyModules.length > 0 && (
                <div className="project-health-warning">
                    <strong>Empty modules detected</strong>
                    <div className="project-health-module-list">
                        {emptyModules.slice(0, 6).map((module) => (
                            <span key={module.id || module.moduleKey}>
                                {module.moduleLabel || module.moduleKey}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {actions.length > 0 && (
                <div className="project-health-actions">
                    <strong>Recommended actions</strong>

                    <div className="project-health-action-list">
                        {actions.map((action) => (
                            <div
                                key={action.key}
                                className={`project-health-action ${action.severity || "info"}`}
                            >
                                <div>
                                    <strong>{action.label}</strong>
                                    <p>{action.message}</p>
                                </div>

                                {action.key === "redeploy-website" ||
                                    action.key === "publish-or-redeploy" ? (
                                    <button
                                        type="button"
                                        className="project-action-btn primary"
                                        disabled={redeploying}
                                        onClick={onRedeploy}
                                    >
                                        {redeploying ? "Queuing..." : "Update Live Site"}
                                    </button>
                                ) : null}

                                {isSuperAdmin && (
                                    action.key === "repair-crm-from-seed" ||
                                    action.key === "repair-missing-crm-items"
                                ) ? (
                                    <button
                                        type="button"
                                        className="project-action-btn primary"
                                        disabled={repairingCrm}
                                        onClick={onRepairCrm}
                                    >
                                        {repairingCrm ? "Repairing..." : "Repair CRM"}
                                    </button>
                                ) : null}

                                {isSuperAdmin && (
                                    action.key === "review-active-seed" ||
                                    action.key === "recreate-crm-seed"
                                ) ? (
                                    <button
                                        type="button"
                                        className="project-action-btn secondary"
                                        onClick={onOpenSeedPicker}
                                    >
                                        Review Seeds
                                    </button>
                                ) : null}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {(health.checks || []).length > 0 && (
                <details className="project-health-checks">
                    <summary>View health checks</summary>

                    <div className="project-health-check-list">
                        {health.checks.map((check) => (
                            <div key={check.key} className={`project-health-check ${check.status}`}>
                                <HealthBadge status={check.status} />
                                <div>
                                    <strong>{check.label}</strong>
                                    <p>{check.message}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </details>
            )}
        </section>
    );
}

function ProjectWorkflowPanel({
    projectId,
    deploymentStatus,
    onRefresh,
    onOpenOperations,
    compact = false
}) {

    const current = deploymentStatus?.status || "idle";
    const failed = current === "failed";

    const steps = [
        { key: "saving", label: "Saving" },
        { key: "syncing", label: "Syncing" },
        { key: "redeploying", label: "Redeploying" },
        { key: "live", label: "Live" }
    ];

    return (
        <section className={`project-panel workflow-panel ${failed ? "workflow-failed" : ""} ${compact ? "workflow-compact" : ""}`}>
            <div className="workflow-header">
                <div>
                    <span className="project-kicker">Workflow</span>
                    <h2>{deploymentStatus?.label || "Deployment Pipeline"}</h2>
                    <p>
                        {failed
                            ? "Deployment needs attention. Review the error message below."
                            : current === "live"
                                ? "Your latest deployment is live."
                                : current === "idle"
                                    ? "No active deployment is running."
                                    : "Your changes are moving through the publish pipeline."}
                    </p>
                </div>

                <div className="workflow-actions">
                    <button
                        type="button"
                        className="project-action-btn secondary"
                        onClick={onRefresh}
                    >
                        Refresh
                    </button>

                    {isSuperAdminUser() && (
                        <ActionButton onClick={onOpenOperations}>
                            Operations
                        </ActionButton>
                    )}
                </div>
            </div>

            <div className="workflow-steps">
                {steps.map(step => {
                    const state = getWorkflowStepState(deploymentStatus, step.key);

                    return (
                        <div key={step.key} className={`workflow-step ${state}`}>
                            <div className="workflow-step-icon">
                                {state === "active" ? "…" : state === "failed" ? "!" : "✓"}
                            </div>
                            <strong>{step.label}</strong>
                        </div>
                    );
                })}
            </div>

            <div className="workflow-grid">
                <div>
                    <span>Sync</span>
                    <strong>{formatPipelineStatus(deploymentStatus?.syncStatus)}</strong>
                </div>

                <div>
                    <span>Redeploy</span>
                    <strong>{formatPipelineStatus(deploymentStatus?.redeployStatus)}</strong>
                </div>

                <div>
                    <span>Module</span>
                    <strong>{deploymentStatus?.moduleKey || "-"}</strong>
                </div>

                <div>
                    <span>Last Live</span>
                    <strong>{formatDateTime(deploymentStatus?.lastRedeployCompletedAtUtc)}</strong>
                </div>
            </div>

            {(deploymentStatus?.syncErrorMessage || deploymentStatus?.redeployErrorMessage) && (
                <div className="workflow-error">
                    {sanitizeErrorMessage(
                        deploymentStatus?.syncErrorMessage || deploymentStatus?.redeployErrorMessage,
                        "Deployment failed. Please retry or contact support if the issue continues."
                    )}
                </div>
            )}
        </section>
    );
}

function SeedPickerModal({ projectId, onClose, onSuccess }) {
    const [seeds, setSeeds] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingSeeds, setLoadingSeeds] = useState(true);
    const [searchText, setSearchText] = useState("");

    useEffect(() => {
        (async () => {
            try {
                setLoadingSeeds(true);
                const data = await getAvailableSeeds(projectId);
                setSeeds(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error(error);
                toast.error("Failed to load available seeds.");
                setSeeds([]);
            } finally {
                setLoadingSeeds(false);
            }
        })();
    }, [projectId]);

    const filteredSeeds = useMemo(() => {
        const query = String(searchText || "").trim().toLowerCase();

        if (!query) return seeds;

        return seeds.filter((seed) => {
            const text = [
                seed.industry,
                seed.subIndustry,
                seed.templatePackKey,
                seed.pattern,
                seed.seedKey,
                seed.status,
                seed.qualityScore
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return text.includes(query);
        });
    }, [seeds, searchText]);

    const handleSelect = async (seedId) => {
        try {
            setIsLoading(true);
            toast.loading("Applying seed...", { id: "seed-apply" });

            await regenerateFromSeed(projectId, seedId);
            await onSuccess();

            toast.success("CRM seed applied.", { id: "seed-apply" });
            onClose();
        } catch (error) {
            console.error(error);
            toast.error(getErrorMessage(error, "Failed to apply seed."), { id: "seed-apply" });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="project-modal-backdrop">
            <div className="project-modal project-seed-picker-modal">
                <div className="project-modal-header sticky">
                    <div>
                        <span className="project-kicker">Vertical Seeds</span>
                        <h3>Select CRM Seed</h3>
                        <p>
                            Search and apply the best approved seed for this project vertical.
                        </p>
                    </div>

                    <button
                        type="button"
                        className="project-modal-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                <div className="project-modal-toolbar">
                    <div className="project-modal-search">
                        <span>Search</span>
                        <input
                            value={searchText}
                            onChange={(event) => setSearchText(event.target.value)}
                            placeholder="Search industry, sub industry, template, pattern..."
                            autoFocus
                        />
                    </div>

                    <div className="project-modal-count-pill">
                        {filteredSeeds.length} of {seeds.length} seeds
                    </div>
                </div>

                <div className="project-modal-scroll">
                    {loadingSeeds ? (
                        <div className="project-loading-card">
                            Loading available seeds...
                        </div>
                    ) : seeds.length === 0 ? (
                        <div className="project-empty">
                            No matching seeds found for this project.
                        </div>
                    ) : filteredSeeds.length === 0 ? (
                        <div className="project-empty">
                            No seeds match your search. Try another industry, template, or pattern.
                        </div>
                    ) : (
                        <div className="seed-grid searchable">
                            {filteredSeeds.map((seed) => (
                                <div key={seed.id} className="seed-card-premium selectable">
                                    <div>
                                        <span>{seed.industry || "Industry"}</span>
                                        <h4>{seed.subIndustry || "Seed"}</h4>
                                        <p>Template: {seed.templatePackKey || "-"}</p>
                                        <p>Pattern: {seed.pattern || "-"}</p>
                                        {seed.qualityScore !== undefined && seed.qualityScore !== null ? (
                                            <p>Quality: {seed.qualityScore}</p>
                                        ) : null}
                                    </div>

                                    <button
                                        type="button"
                                        disabled={isLoading}
                                        onClick={() => handleSelect(seed.id)}
                                    >
                                        {isLoading ? "Applying..." : "Use this seed"}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function buildSectionPreviewHtml(project, variant) {
    const title = variant?.title || project?.businessName || "Preview";
    const subtitle = variant?.subtitle || project?.description || "Preview your improved section.";
    const cta = variant?.primaryCta || project?.primaryCallToAction || "Get Started";
    const imageUrl = variant?.imageUrl || "";

    return `
<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width,initial-scale=1" />
<style>
    * { box-sizing: border-box; }
    body {
        margin: 0;
        font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        background: #020617;
        color: white;
    }
    .hero {
        min-height: 420px;
        display: grid;
        grid-template-columns: 1.1fr .9fr;
        gap: 28px;
        align-items: center;
        padding: 44px;
        background:
            radial-gradient(circle at top left, rgba(59,130,246,.35), transparent 34%),
            linear-gradient(135deg, #0f172a, #111827);
    }
    .kicker {
        color: #93c5fd;
        text-transform: uppercase;
        letter-spacing: .14em;
        font-size: 12px;
        font-weight: 900;
    }
    h1 {
        margin: 12px 0;
        font-size: 46px;
        line-height: 1;
        letter-spacing: -.05em;
    }
    p {
        color: #cbd5e1;
        line-height: 1.65;
        font-size: 16px;
        margin: 0 0 22px;
    }
    .cta {
        display: inline-flex;
        min-height: 46px;
        align-items: center;
        justify-content: center;
        padding: 0 18px;
        border-radius: 999px;
        background: linear-gradient(135deg, #2563eb, #7c3aed);
        color: white;
        font-weight: 900;
        text-decoration: none;
    }
    .visual {
        min-height: 300px;
        border-radius: 28px;
        overflow: hidden;
        border: 1px solid rgba(255,255,255,.12);
        background: rgba(255,255,255,.06);
        box-shadow: 0 24px 70px rgba(0,0,0,.35);
    }
    .visual img {
        width: 100%;
        height: 100%;
        min-height: 300px;
        object-fit: cover;
        display: block;
    }
    .visual-fallback {
        min-height: 300px;
        display: grid;
        place-items: center;
        color: #93c5fd;
        font-weight: 900;
    }
    @media (max-width: 760px) {
        .hero { grid-template-columns: 1fr; padding: 28px; }
        h1 { font-size: 34px; }
    }
</style>
</head>
<body>
<section class="hero">
    <div>
        <div class="kicker">${project?.industry || "Shriansh Nexus"}</div>
        <h1>${title}</h1>
        <p>${subtitle}</p>
        <a class="cta" href="#">${cta}</a>
    </div>
    <div class="visual">
        ${imageUrl
            ? `<img src="${imageUrl}" alt="">`
            : `<div class="visual-fallback">Image preview will appear here</div>`
        }
    </div>
</section>
</body>
</html>`;
}

function SectionPreviewModal({
    project,
    preview,
    selectedVariant,
    onSelect,
    onApply,
    onClose,
    applying
}) {
    if (!preview) return null;

    return (
        <div className="project-modal-backdrop section-preview-backdrop">
            <div className="section-preview-studio">
                <div className="section-preview-studio-head">
                    <div>
                        <span className="project-kicker">AI Section Studio</span>
                        <h2>Choose Best {preview.target} Variant</h2>
                        <p>Preview AI-generated section options before applying anything to the live site.</p>
                    </div>

                    <button type="button" onClick={onClose}>×</button>
                </div>

                <div className="section-preview-studio-grid">
                    <div className="section-variant-list">
                        {(preview.variants || []).map(v => (
                            <button
                                type="button"
                                key={v.variant}
                                className={`section-variant-option ${selectedVariant?.variant === v.variant ? "active" : ""}`}
                                onClick={() => onSelect(v)}
                            >
                                <span>{v.previewLabel || `Variant ${v.variant}`}</span>
                                <strong>{v.title}</strong>
                                <small>{v.reason || "AI generated option"}</small>
                            </button>
                        ))}
                    </div>

                    <div className="section-live-preview-panel">
                        {selectedVariant ? (
                            <>
                                <div className="section-live-preview-toolbar">
                                    <div>
                                        <span>{selectedVariant.previewLabel || `Variant ${selectedVariant.variant}`}</span>
                                        <strong>{selectedVariant.title}</strong>
                                    </div>

                                    <button
                                        type="button"
                                        className="project-action-btn primary"
                                        disabled={applying}
                                        onClick={() => onApply(selectedVariant)}
                                    >
                                        {applying ? "Applying..." : "Apply & Deploy"}
                                    </button>
                                </div>

                                <iframe
                                    title="Section variant preview"
                                    className="section-live-preview-frame"
                                    srcDoc={buildSectionPreviewHtml(project, selectedVariant)}
                                />

                                <div className="section-preview-reason">
                                    <strong>Why this option?</strong>
                                    <p>{selectedVariant.reason || "This variant improves clarity, trust, and conversion flow."}</p>
                                </div>
                            </>
                        ) : (
                            <div className="project-empty">Select a variant to preview.</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function resolveFixTarget(moduleKey = "") {
    const key = moduleKey.toLowerCase();

    if (key.includes("product")) return "products";
    if (key.includes("program")) return "programs";
    if (key.includes("property")) return "listings";
    if (key.includes("listing")) return "listings";
    if (key.includes("doctor")) return "doctors";
    if (key.includes("treatment")) return "treatments";
    if (key.includes("feature")) return "features";
    if (key.includes("pricing")) return "pricing";
    if (key.includes("practice")) return "practice";
    if (key.includes("team")) return "team";

    return key;
}

function normalizeModuleKey(value = "") {
    return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[_\s]+/g, "-");
}

function getModuleIcon(moduleKey = "", moduleLabel = "") {
    const key = normalizeModuleKey(moduleKey);
    const label = String(moduleLabel || "").toLowerCase();

    if (key.includes("program") || label.includes("program")) return "🎓";
    if (key.includes("coach") || label.includes("coach")) return "🧑‍🏫";
    if (key.includes("testimonial") || label.includes("testimonial")) return "⭐";
    if (key.includes("faq") || label.includes("faq")) return "❓";
    if (key.includes("listing") || key.includes("property") || label.includes("listing") || label.includes("property")) return "🏠";
    if (key.includes("product") || label.includes("product")) return "🛍";
    if (key.includes("service") || label.includes("service")) return "🧩";
    if (key.includes("team") || label.includes("team")) return "👥";
    if (key.includes("doctor") || label.includes("doctor")) return "🩺";
    if (key.includes("treatment") || label.includes("treatment")) return "💊";
    if (key.includes("gallery") || label.includes("gallery")) return "🖼";
    if (key.includes("lead") || label.includes("lead")) return "📩";

    return "📦";
}

function getProjectFallbackCount(project, moduleKey = "") {
    const key = normalizeModuleKey(moduleKey);

    if (key.includes("program")) return project?.programCount ?? 0;
    if (key.includes("listing") || key.includes("property")) return project?.propertyListingCount ?? 0;
    if (key.includes("product")) return project?.productCount ?? 0;
    if (key.includes("testimonial")) return project?.testimonialCount ?? 0;
    if (key.includes("faq")) return project?.faqCount ?? 0;

    // Backend does not yet expose coachCount on ProjectDetailDto.
    // We will add proper module PublishedItemCount later.
    return 0;
}

function getModuleCount(module, project) {
    if (typeof module?.publishedItemCount === "number") {
        return module.publishedItemCount;
    }

    if (typeof module?.itemCount === "number") {
        return module.itemCount;
    }

    const items = Array.isArray(module?.items) ? module.items : [];

    if (items.length > 0) {
        return items.filter(x => x?.isPublished !== false).length;
    }

    return getProjectFallbackCount(project, module?.moduleKey);
}

function buildDynamicStatCards(project, modules) {
    const moduleCards = (modules || [])
        .filter(m => {
            const type = String(m?.moduleType || "").toLowerCase();

            // Show only meaningful website content modules.
            if (type && type !== "collection") return false;

            const key = normalizeModuleKey(m?.moduleKey);
            const label = String(m?.moduleLabel || "").trim();

            if (!key && !label) return false;

            return true;
        })
        .map(m => ({
            key: normalizeModuleKey(m.moduleKey || m.moduleLabel),
            label: m.moduleLabel || m.moduleKey || "Module",
            value: getModuleCount(m, project),
            icon: getModuleIcon(m.moduleKey, m.moduleLabel)
        }));

    // If modules are still loading or missing, keep safe project fallback.
    if (moduleCards.length > 0) {
        return moduleCards;
    }

    return [
        {
            key: "programs",
            label: "Programs",
            value: project?.programCount ?? 0,
            icon: "🎓"
        },
        {
            key: "testimonials",
            label: "Testimonials",
            value: project?.testimonialCount ?? 0,
            icon: "⭐"
        },
        {
            key: "faqs",
            label: "FAQs",
            value: project?.faqCount ?? 0,
            icon: "❓"
        }
    ].filter(x => Number(x.value || 0) > 0);
}

function confirmWithToast({
    id,
    title,
    message,
    confirmLabel = "Continue",
    cancelLabel = "Cancel",
    onConfirm
}) {
    toast.custom(
        (t) => (
            <div className="toast-confirm-card">
                <strong>{title}</strong>
                <p>{message}</p>

                <div className="toast-confirm-actions">
                    <button
                        type="button"
                        onClick={() => {
                            toast.dismiss(t.id);
                            onConfirm?.();
                        }}
                    >
                        {confirmLabel}
                    </button>

                    <button
                        type="button"
                        className="secondary"
                        onClick={() => toast.dismiss(t.id)}
                    >
                        {cancelLabel}
                    </button>
                </div>
            </div>
        ),
        {
            id,
            duration: 10000
        }
    );
}

function ProjectDetailsModal({ project, resolvedProjectId, onClose }) {
    if (!project) return null;

    const rows = [
        ["Project ID", resolvedProjectId],
        ["Account", project.accountName || project.accountEmail || project.accountId || "-"],
        ["Industry", project.industry || "-"],
        ["Sub Industry", project.subIndustry || "-"],
        ["Status", project.status || "-"],
        ["Published", project.isPublished ? "Yes" : "No"],
        ["Theme", project.themeKey || "-"],
        ["Brand Style", project.brandStyle || "-"],
        ["Tone", project.toneOfVoice || "-"],
        ["Price Positioning", project.pricePositioning || "-"]
    ];

    return (
        <div className="project-modal-backdrop">
            <div className="project-modal project-details-modal">
                <div className="project-modal-header">
                    <div>
                        <span className="project-kicker">Project Details</span>
                        <h3>{project.businessName || "Project Metadata"}</h3>
                        <p>
                            Technical and business configuration for this generated website.
                        </p>
                    </div>

                    <button type="button" onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className="project-details-grid">
                    {rows.map(([label, value]) => (
                        <div key={label} className="project-details-row">
                            <span>{label}</span>
                            <strong>{value || "-"}</strong>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function normalizePremiumUpgradePayload(payload) {
    const root = payload?.data || payload || {};

    const rawTargets =
        Array.isArray(root.targets) ? root.targets :
            Array.isArray(root.recommendedTargets) ? root.recommendedTargets :
                Array.isArray(root.recommendations) ? root.recommendations :
                    Array.isArray(root.sections) ? root.sections :
                        Array.isArray(root.items) ? root.items :
                            [];

    const targets = rawTargets.map((item, index) => {
        const target =
            item.target ||
            item.sectionKey ||
            item.section ||
            item.key ||
            item.moduleKey ||
            `target-${index + 1}`;

        return {
            ...item,
            target,
            label:
                item.label ||
                item.title ||
                item.name ||
                target,
            reason:
                item.reason ||
                item.description ||
                item.summary ||
                item.suggestedFix ||
                item.recommendation ||
                "AI recommends improving this section for better visual polish and conversion.",
            recommendedVariant:
                item.recommendedVariant ||
                item.variant ||
                item.variantId ||
                1
        };
    });

    return {
        ...root,
        summary:
            root.summary ||
            root.message ||
            root.upgradeSummary ||
            root.previewSummary ||
            "Premium preview generated. Review recommended improvements and apply when ready.",
        targets
    };
}

function getHealthTone(status) {
    if (status === "failed") return "danger";
    if (status === "warning" || status === "running" || status === "idle") return "warning";
    if (status === "ok") return "success";
    return "neutral";
}

function formatHealthStatus(status) {
    if (!status) return "Unknown";
    return String(status)
        .replace(/[-_]/g, " ")
        .replace(/\b\w/g, (x) => x.toUpperCase());
}

function normalizeStatus(value = "") {
    return String(value || "").trim().toLowerCase();
}

function isDeploymentBusy(deploymentStatus) {
    const status = normalizeStatus(deploymentStatus?.status);
    const syncStatus = normalizeStatus(deploymentStatus?.syncStatus);
    const redeployStatus = normalizeStatus(deploymentStatus?.redeployStatus);

    return (
        status === "syncing" ||
        status === "redeploying" ||
        syncStatus === "pending" ||
        syncStatus === "running" ||
        redeployStatus === "pending" ||
        redeployStatus === "running"
    );
}

function isDeploymentFailed(deploymentStatus, health) {
    return (
        normalizeStatus(deploymentStatus?.status) === "failed" ||
        normalizeStatus(deploymentStatus?.syncStatus) === "failed" ||
        normalizeStatus(deploymentStatus?.redeployStatus) === "failed" ||
        normalizeStatus(health?.deployment?.status) === "failed"
    );
}

function isDeploymentLive(deploymentStatus) {
    return (
        normalizeStatus(deploymentStatus?.status) === "live" ||
        normalizeStatus(deploymentStatus?.redeployStatus) === "completed"
    );
}

function getProjectLiveUrl(project) {
    return (
        project?.liveUrl ||
        project?.LiveUrl ||
        project?.publishedUrl ||
        project?.siteUrl ||
        ""
    );
}

function getProjectPublishState(project, deploymentStatus, health) {
    const liveUrl = getProjectLiveUrl(project);
    const hasLiveUrl = !!liveUrl;
    const busy = isDeploymentBusy(deploymentStatus);
    const failed = isDeploymentFailed(deploymentStatus, health);
    const deploymentLive = isDeploymentLive(deploymentStatus);

    const isPublished =
        hasLiveUrl ||
        project?.isPublished === true ||
        project?.publishedAtUtc ||
        project?.publishedAt ||
        deploymentLive;

    if (failed) {
        return {
            key: "failed",
            tone: "danger",
            title: "Deployment needs attention",
            message: hasLiveUrl
                ? "A previous live site exists, but the latest publish/update failed. The live site may show an older version."
                : "The latest publish/update failed. Review the workflow error before publishing again.",
            liveUrl,
            showLiveLink: hasLiveUrl,
            primaryAction: hasLiveUrl ? "Update Live Site" : "Retry Publish",
            canUpdate: true,
            canPublish: !hasLiveUrl
        };
    }

    if (busy) {
        return {
            key: "running",
            tone: "warning",
            title: hasLiveUrl ? "Live update in progress" : "Publishing in progress",
            message: hasLiveUrl
                ? "A redeploy is queued or running. The live site may still show the previous version for a moment."
                : "The first publish is queued or running. Live URL will appear after deployment completes.",
            liveUrl,
            showLiveLink: hasLiveUrl,
            primaryAction: "Refresh Status",
            canUpdate: false,
            canPublish: false
        };
    }

    if (hasLiveUrl && isPublished) {
        return {
            key: "live",
            tone: "success",
            title: "Published",
            message: "Live website is available. Use Update Live Site after CRM/content changes.",
            liveUrl,
            showLiveLink: true,
            primaryAction: "Update Live Site",
            canUpdate: true,
            canPublish: false
        };
    }

    if (!hasLiveUrl && deploymentLive) {
        return {
            key: "metadata-missing",
            tone: "warning",
            title: "Deployment completed, Live URL missing",
            message: "A redeploy completed, but project live metadata was not saved. Run update once to refresh live site metadata.",
            liveUrl,
            showLiveLink: false,
            primaryAction: "Refresh / Update",
            canUpdate: true,
            canPublish: false
        };
    }

    if (normalizeStatus(deploymentStatus?.status) === "synced") {
        return {
            key: "synced",
            tone: "warning",
            title: "CRM synced, not live yet",
            message: "CRM content is prepared. Publish or update the live website to make it visible.",
            liveUrl,
            showLiveLink: false,
            primaryAction: "Publish Site",
            canUpdate: false,
            canPublish: true
        };
    }

    return {
        key: "not-published",
        tone: "neutral",
        title: "Not Published",
        message: "This project is generated but does not have a live website yet.",
        liveUrl,
        showLiveLink: false,
        primaryAction: "Publish Site",
        canUpdate: false,
        canPublish: true
    };
}

function ProjectLiveStatusCard({
    project,
    deploymentStatus,
    health,
    publishing,
    updating,
    onPublish,
    onUpdate,
    onRefresh
}) {
    const state = getProjectPublishState(project, deploymentStatus, health);
    const busy = publishing || updating || isDeploymentBusy(deploymentStatus);

    return (
        <div className={`project-live-card project-live-card-v2 live-state-${state.tone}`}>
            <span>Live Website</span>

            <strong>{state.title}</strong>
            <p>{state.message}</p>

            {state.showLiveLink ? (
                <a href={state.liveUrl} target="_blank" rel="noreferrer">
                    View Live Site →
                </a>
            ) : null}

            <small>
                Last live:{" "}
                {deploymentStatus?.lastRedeployCompletedAtUtc
                    ? new Date(deploymentStatus.lastRedeployCompletedAtUtc).toLocaleString()
                    : project?.publishedAtUtc
                        ? new Date(project.publishedAtUtc).toLocaleString()
                        : "Not available"}
            </small>

            <div className="project-live-actions-row">
                {state.canPublish ? (
                    <button
                        type="button"
                        className="project-live-update-btn"
                        disabled={busy}
                        onClick={onPublish}
                    >
                        {publishing ? "Publishing..." : state.primaryAction}
                    </button>
                ) : state.canUpdate ? (
                    <button
                        type="button"
                        className="project-live-update-btn"
                        disabled={busy}
                        onClick={onUpdate}
                    >
                        {updating ? "Updating..." : state.primaryAction}
                    </button>
                ) : (
                    <button
                        type="button"
                        className="project-live-update-btn"
                        disabled={busy}
                        onClick={onRefresh}
                    >
                        Refresh Status
                    </button>
                )}
            </div>
        </div>
    );
}

function HealthBadge({ status }) {
    const tone = getHealthTone(status);

    return (
        <span className={`health-badge ${tone}`}>
            {formatHealthStatus(status)}
        </span>
    );
}

function LeadMiniStat({ label, value, tone = "" }) {
    return (
        <div className={`project-lead-mini-stat ${tone}`}>
            <span>{label}</span>
            <strong>{value || 0}</strong>
        </div>
    );
}

function ProjectLeadSummaryPanel({ summary, loading, onRefresh }) {
    const recent = summary?.recentLeads || [];
    const hasLeads = Number(summary?.totalLeads || 0) > 0;

    return (
        <section className="project-panel project-lead-summary-panel">
            <div className="project-panel-header">
                <div>
                    <span className="project-kicker">Lead Performance</span>
                    <h2>Project Leads</h2>
                    <p>
                        Track enquiries generated from this website, including source page, CTA,
                        module, and recent pipeline status.
                    </p>
                </div>

                <button
                    type="button"
                    className="project-action-btn secondary"
                    onClick={onRefresh}
                    disabled={loading}
                >
                    {loading ? "Refreshing..." : "Refresh"}
                </button>
            </div>

            <div className="project-lead-stats-grid">
                <LeadMiniStat label="Total" value={summary?.totalLeads} />
                <LeadMiniStat label="New" value={summary?.newLeads} tone="new" />
                <LeadMiniStat label="Contacted" value={summary?.contactedLeads} tone="contacted" />
                <LeadMiniStat label="Won" value={summary?.wonLeads} tone="won" />
                <LeadMiniStat label="Lost" value={summary?.lostLeads} tone="lost" />
            </div>

            {hasLeads ? (
                <>
                    <div className="project-lead-insights-grid">
                        <div>
                            <span>Top Source</span>
                            <strong>{summary?.topSource || "-"}</strong>
                        </div>

                        <div>
                            <span>Top Page</span>
                            <strong>{summary?.topPageKey || "-"}</strong>
                        </div>

                        <div>
                            <span>Top Module</span>
                            <strong>{summary?.topModuleKey || "-"}</strong>
                        </div>

                        <div>
                            <span>Top CTA</span>
                            <strong>{summary?.topCtaLabel || "-"}</strong>
                        </div>
                    </div>

                    <div className="project-lead-breakdown-row">
                        <LeadBreakdown title="Sources" items={summary?.sources} />
                        <LeadBreakdown title="Pages" items={summary?.pages} />
                        <LeadBreakdown title="CTAs" items={summary?.ctas} />
                    </div>

                    <div className="project-lead-recent">
                        <div className="project-lead-recent-head">
                            <strong>Recent Leads</strong>
                            <Link to="/leads">View all leads →</Link>
                        </div>

                        {recent.length === 0 ? (
                            <div className="project-empty small">
                                No recent leads found.
                            </div>
                        ) : (
                            <div className="project-lead-recent-list">
                                {recent.map((lead) => (
                                    <div key={lead.id} className="project-lead-recent-item">
                                        <div>
                                            <strong>{lead.fullName || "Unnamed Lead"}</strong>
                                            <p>
                                                {[lead.ctaLabel, lead.pageKey, lead.moduleKey]
                                                    .filter(Boolean)
                                                    .join(" • ") || lead.source || "Website enquiry"}
                                            </p>
                                        </div>

                                        <span className={`lead-status-pill ${String(lead.status || "New").toLowerCase()}`}>
                                            {lead.status || "New"}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <div className="project-empty">
                    No leads yet. Once visitors submit forms on the live website, project-specific lead
                    performance will appear here.
                </div>
            )}
        </section>
    );
}

function LeadBreakdown({ title, items }) {
    const list = Array.isArray(items) ? items : [];

    return (
        <div className="project-lead-breakdown-card">
            <strong>{title}</strong>

            {list.length === 0 ? (
                <p>No data yet</p>
            ) : (
                <div className="project-lead-breakdown-list">
                    {list.slice(0, 5).map((item) => (
                        <div key={item.key}>
                            <span>{item.key || "-"}</span>
                            <strong>{item.count || 0}</strong>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function CrmProjectDetailPage() {
    const { projectId } = useParams();

    const [project, setProject] = useState(null);
    const [modules, setModules] = useState([]);
    const [deploymentStatus, setDeploymentStatus] = useState(null);
    const [showWorkflowPanel, setShowWorkflowPanel] = useState(false);
    const [showOperationsModal, setShowOperationsModal] = useState(false);
    const workflowCompletionToastShownRef = useRef(false);
    const [pageMessage, setPageMessage] = useState("");

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [publishingSite, setPublishingSite] = useState(false);
    const [showSeedModal, setShowSeedModal] = useState(false);
    const [regeneratingWebsite, setRegeneratingWebsite] = useState(false);
    const [repairingWebsite, setRepairingWebsite] = useState(false);
    const [fixingSection, setFixingSection] = useState("");

    const [sectionPreview, setSectionPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [selectedSectionVariant, setSelectedSectionVariant] = useState(null);
    const [applyingSectionVariant, setApplyingSectionVariant] = useState(false);

    const [variantStats, setVariantStats] = useState(null);
    const [variantBusy, setVariantBusy] = useState(false);

    const [deploymentVersions, setDeploymentVersions] = useState([]);
    const [loadingVersions, setLoadingVersions] = useState(false);
    const [restoringVersion, setRestoringVersion] = useState("");

    const [productionFit, setProductionFit] = useState(null);
    const [productionFitLoading, setProductionFitLoading] = useState(false);
    const [productionFitAnalyzing, setProductionFitAnalyzing] = useState(false);

    const [premiumUpgrade, setPremiumUpgrade] = useState(null);
    const [premiumLoading, setPremiumLoading] = useState(false);
    const [premiumGenerating, setPremiumGenerating] = useState(false);
    const [premiumApplying, setPremiumApplying] = useState(false);

    const [productionFitFixing, setProductionFitFixing] = useState(false);
    const [showProjectDetails, setShowProjectDetails] = useState(false);
    const [showDeploymentRecovery, setShowDeploymentRecovery] = useState(false);
    const [showSiteOperations, setShowSiteOperations] = useState(false);
    const [showAiStudio, setShowAiStudio] = useState(false);
    const [showVariantPerformance, setShowVariantPerformance] = useState(false);
    const [showCustomDomains, setShowCustomDomains] = useState(false);

    const [projectHealth, setProjectHealth] = useState(null);
    const [healthLoading, setHealthLoading] = useState(false);
    const [repairingCrm, setRepairingCrm] = useState(false);

    const [projectLeadSummary, setProjectLeadSummary] = useState(null);
    const [leadSummaryLoading, setLeadSummaryLoading] = useState(false);

    const [accounts, setAccounts] = useState([]);

    const isSuperAdmin = isSuperAdminUser();
    const canRunDiagnostics = canUseProjectDiagnostics();
    const canPreviewUpgrade = canPreviewProjectUpgrade();
    const canApplyUpgrade = canApplyProjectUpgrade();
    const canUseAiStudio = canUseProjectAiStudio();
    const canRepairCrmSeed = canRepairCrmFromSeed();
    const canAssignAccounts = canAssignProjectAccounts();

    const canOpenSiteOperations =
        isSuperAdmin ||
        (project?.status === "Generated" && (!project?.isPublished || !project?.liveUrl));

    const resolvedProjectId = project?.id || project?.projectId || projectId;

    const dynamicStatCards = useMemo(() => {
        return buildDynamicStatCards(project, modules);
    }, [project, modules]);


    const showPageMessage = useCallback((text, options = {}) => {
        const { sticky = false } = options;

        setPageMessage(text || "");

        if (!sticky && text && !String(text).toLowerCase().includes("failed")) {
            setTimeout(() => {
                setPageMessage("");
            }, 4500);
        }
    }, []);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError("");
            const data = await getCrmProjectDetail(projectId);

            if (canAssignAccounts) {
                try {
                    const accountList = await getAccounts();
                    setAccounts(Array.isArray(accountList) ? accountList.filter(x => !x.isSuperAdmin) : []);
                } catch (err) {
                    console.error("Failed to load accounts", err);
                }
            }

            setProject(data || null);
        } catch (err) {
            console.error(err);
            setError(getErrorMessage(err, "Failed to load CRM project detail."));
        } finally {
            setLoading(false);
        }
    }, [projectId, canAssignAccounts]);

    const loadDeploymentStatus = useCallback(async () => {
        if (!projectId) return;

        try {
            const data = await getDeploymentStatus(projectId);
            setDeploymentStatus(data || null);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load deployment status.");
        }
    }, [projectId]);

    useEffect(() => {
        if (project?.businessName) {
            window.__activeProjectName = project.businessName;
        }
    }, [project]);

    const loadProductionFit = useCallback(async () => {
        if (!projectId || !canRunDiagnostics) return;

        try {
            setProductionFitLoading(true);

            const result = await getProductionFit(projectId);
            setProductionFit(result);
        } catch (err) {
            console.error(err);
        } finally {
            setProductionFitLoading(false);
        }
    }, [projectId, canRunDiagnostics]);

    useEffect(() => {
        loadProductionFit();
    }, [loadProductionFit]);

    const loadPremiumUpgrade = useCallback(async () => {
        if (!projectId || !canPreviewUpgrade) return;

        try {
            setPremiumLoading(true);
            const result = await getPremiumUpgradePreview(projectId);
            setPremiumUpgrade(normalizePremiumUpgradePayload(result));
        } catch (err) {
            console.error(err);
        } finally {
            setPremiumLoading(false);
        }
    }, [projectId, canPreviewUpgrade]);

    useEffect(() => {
        loadPremiumUpgrade();
    }, [loadPremiumUpgrade]);

    const loadProjectHealth = useCallback(async () => {
        if (!projectId) return;

        try {
            setHealthLoading(true);
            const data = await getProjectHealth(projectId);
            setProjectHealth(data);
        } catch (error) {
            console.error(error);
            toast.error(getErrorMessage(error, "Failed to load project health."));
        } finally {
            setHealthLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        loadProjectHealth();
    }, [loadProjectHealth]);

    const loadProjectLeadSummary = useCallback(async () => {
        if (!projectId) return;

        try {
            setLeadSummaryLoading(true);
            const data = await getProjectLeadSummary(projectId);
            setProjectLeadSummary(data || null);
        } catch (error) {
            console.error(error);
            toast.error(getErrorMessage(error, "Failed to load project lead summary."));
        } finally {
            setLeadSummaryLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        loadProjectLeadSummary();
    }, [loadProjectLeadSummary]);

    const handleRepairCrmFromSeed = async () => {

        if (!canRepairCrmSeed) {
            toast.error("Only platform admins can repair CRM from seed.");
            return;
        }

        if (!resolvedProjectId) return;

        confirmWithToast({
            id: "confirm-repair-crm-from-seed",
            title: "Repair CRM from seed?",
            message: "This will only create missing modules/items from the active seed. It will not overwrite user-edited CRM data.",
            confirmLabel: "Repair CRM",
            onConfirm: async () => {
                try {
                    workflowCompletionToastShownRef.current = false;
                    setShowWorkflowPanel(true);
                    setRepairingCrm(true);

                    toast.loading("Repairing CRM from seed...", { id: "repair-crm-seed" });

                    const result = await repairCrmFromSeed(resolvedProjectId, {
                        mode: "missing-only",
                        queueRedeploy: true
                    });

                    toast.success(
                        result?.message || "CRM repair completed and redeploy queued.",
                        { id: "repair-crm-seed" }
                    );

                    showPageMessage(
                        result?.redeployQueued
                            ? "CRM repair completed. Live website update is now in progress."
                            : "CRM repair completed."
                    );

                    await refreshAll();
                    await pollDeploymentStatus();

                    setTimeout(loadProjectHealth, 3000);
                    setTimeout(loadProjectHealth, 7000);
                    setTimeout(loadProjectHealth, 12000);
                } catch (err) {
                    console.error(err);
                    toast.error(
                        getErrorMessage(err, "Failed to repair CRM from seed."),
                        { id: "repair-crm-seed" }
                    );
                } finally {
                    setRepairingCrm(false);
                }
            }
        });
    };

    const handleFixProductionIssues = async () => {

        if (!canRunDiagnostics) {
            toast.error("You do not have permission to run project quality fixes.");
            return;
        }

        if (!projectId) return;

        try {
            workflowCompletionToastShownRef.current = false;
            setShowWorkflowPanel(true);
            setProductionFitFixing(true);
            toast.loading("Fixing production issues...", { id: "production-fit-fix" });

            const result = await fixProductionFitIssues(projectId);

            toast.success(
                result?.message || "Production issues fix queued.",
                { id: "production-fit-fix" }
            );
            showPageMessage(result?.message || "Production issues fix queued. Track progress in the workflow panel.");

            await loadProductionFit();
            await refreshAll();

            setTimeout(() => loadDeploymentStatus(), 2000);
            setTimeout(() => loadDeploymentStatus(), 6000);
            setTimeout(() => loadDeploymentStatus(), 12000);
        } catch (err) {
            console.error(err);
            toast.error(
                getErrorMessage(err, "Failed to fix production issues."),
                { id: "production-fit-fix" }
            );
        } finally {
            setProductionFitFixing(false);
        }
    };

    const handleOpenPremiumTargetPreview = (target) => {
        const variants = target?.preview?.variants || [];

        if (!variants.length) {
            toast.error("No preview variants found for this target.");
            return;
        }

        setShowAiStudio(false);

        setSectionPreview({
            target: target.target,
            scope: target.preview?.scope,
            variants
        });

        const recommendedVariant = target.recommendedVariant || variants[0]?.variant;
        const selected =
            variants.find(v => Number(v.variant) === Number(recommendedVariant)) ||
            variants[0] ||
            null;

        setSelectedSectionVariant(selected);
    };

    const handleGeneratePremiumPreview = async () => {

        if (!canPreviewUpgrade) {
            toast.error("You do not have permission to preview premium upgrades.");
            return;
        }

        if (!projectId) return;

        try {
            setPremiumGenerating(true);
            toast.loading("Generating premium preview...", { id: "premium-upgrade" });

            const result = await generatePremiumUpgradePreview(projectId);
            const normalized = normalizePremiumUpgradePayload(result);

            setPremiumUpgrade(normalized);

            if (normalized.targets.length > 0) {
                toast.success(`Premium preview generated: ${normalized.targets.length} sections ready.`, {
                    id: "premium-upgrade"
                });
            } else {
                toast.success("Premium preview generated, but no section targets were returned.", {
                    id: "premium-upgrade"
                });
            }
        } catch (err) {
            console.error(err);
            toast.error(getErrorMessage(err, "Premium preview failed."), { id: "premium-upgrade" });
        } finally {
            setPremiumGenerating(false);
        }
    };

    const handleApplyPremiumUpgrade = async () => {

        if (!canApplyUpgrade) {
            toast.error("You do not have permission to apply premium upgrades.");
            return;
        }

        if (!projectId) return;

        try {
            workflowCompletionToastShownRef.current = false;
            setShowWorkflowPanel(true);
            setPremiumApplying(true);
            toast.loading("Applying premium upgrade...", { id: "premium-upgrade" });

            await applyPremiumUpgrade(projectId, { redeploy: true });

            toast.success("Premium upgrade applied and redeploy queued.", { id: "premium-upgrade" });
            showPageMessage("Premium upgrade applied. Live website update is now in progress.");

            await loadPremiumUpgrade();
            await refreshAll();

            setTimeout(() => loadDeploymentStatus(), 2000);
            setTimeout(() => loadDeploymentStatus(), 6000);
            setTimeout(() => loadDeploymentStatus(), 12000);
        } catch (err) {
            console.error(err);
            toast.error(getErrorMessage(err, "Premium upgrade failed."), { id: "premium-upgrade" });
        } finally {
            setPremiumApplying(false);
        }
    };


    const handleAnalyzeProductionFit = async () => {

        if (!canRunDiagnostics) {
            toast.error("You do not have permission to run project quality checks.");
            return;
        }

        if (!projectId) return;

        try {
            setProductionFitAnalyzing(true);
            toast.loading("Analyzing production fit...", { id: "production-fit" });

            const result = await analyzeProductionFit(projectId);

            setProductionFit(result);
            toast.success("Production fit analysis completed.", { id: "production-fit" });
        } catch (err) {
            console.error(err);
            toast.error(getErrorMessage(err, "Production fit analysis failed."), { id: "production-fit" });
        } finally {
            setProductionFitAnalyzing(false);
        }
    };

    const loadDeploymentVersions = useCallback(async () => {
        if (!projectId) return;

        try {
            setLoadingVersions(true);
            const data = await getDeploymentVersions(projectId);
            setDeploymentVersions(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load deployment versions.");
        } finally {
            setLoadingVersions(false);
        }
    }, [projectId]);

    const handleRollbackVersion = async (version) => {
        if (!resolvedProjectId || !version) return;

        confirmWithToast({
            id: `confirm-rollback-${version}`,
            title: "Restore deployment version?",
            message: `This will switch the live site back to ${version}. CRM data will not be changed.`,
            confirmLabel: "Restore",
            onConfirm: async () => {
                try {
                    setRestoringVersion(version);
                    toast.loading(`Restoring ${version}...`, { id: "rollback-version" });

                    await rollbackDeploymentVersion(resolvedProjectId, version);

                    await Promise.all([
                        loadDeploymentVersions(),
                        loadDeploymentStatus(),
                        loadProjectHealth(),
                        load()
                    ]);

                    toast.success(`Restored ${version}.`, { id: "rollback-version" });
                } catch (err) {
                    console.error(err);
                    toast.error(getErrorMessage(err, "Rollback failed."), { id: "rollback-version" });
                } finally {
                    setRestoringVersion("");
                }
            }
        });
    };

    const loadVariantStats = useCallback(async () => {
        try {
            const data = await getVariantStats();
            const match = (data || []).find(x => x.projectId === projectId);
            setVariantStats(match || null);
        } catch (err) {
            console.error(err);
        }
    }, [projectId]);

    const loadModules = useCallback(async () => {
        try {
            const data = await getModules(projectId);
            setModules(data || []);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load project modules.");
        }
    }, [projectId]);

    useEffect(() => {
        load();
        loadModules();
        loadVariantStats();
        loadDeploymentStatus();
        loadDeploymentVersions();
        loadProjectHealth();
        loadProjectLeadSummary();
    }, [load, loadModules, loadVariantStats, loadDeploymentStatus, loadDeploymentVersions, loadProjectHealth, loadProjectLeadSummary]);

    useEffect(() => {
        if (!showWorkflowPanel) return undefined;

        const current = deploymentStatus?.status || "idle";

        if (current === "failed") {
            workflowCompletionToastShownRef.current = false;

            showPageMessage("Deployment failed. Please review the workflow error.", {
                sticky: true
            });

            return undefined;
        }

        if (current === "live") {
            if (!workflowCompletionToastShownRef.current) {
                workflowCompletionToastShownRef.current = true;

                const moduleKey = deploymentStatus?.moduleKey;
                const message = moduleKey
                    ? `Deployment completed for ${moduleKey}. Live website is updated.`
                    : "Deployment completed. Live website is updated.";

                showPageMessage(message);
            }

            const timer = setTimeout(() => {
                setShowWorkflowPanel(false);
            }, 4500);

            return () => clearTimeout(timer);
        }

        return undefined;
    }, [
        deploymentStatus?.status,
        deploymentStatus?.moduleKey,
        showWorkflowPanel,
        showPageMessage
    ]);

    const refreshAll = async () => {
        await Promise.all([
            load(),
            loadModules(),
            loadVariantStats(),
            loadDeploymentStatus(),
            loadDeploymentVersions(),
            loadProjectHealth(),
            loadProjectLeadSummary()
        ]);
    };

    const pollDeploymentStatus = async () => {
        await loadDeploymentStatus();
        await loadProjectHealth();

        setTimeout(loadDeploymentStatus, 1500);
        setTimeout(loadDeploymentStatus, 4000);
        setTimeout(loadDeploymentStatus, 8000);
        setTimeout(loadDeploymentStatus, 12000);
    };

    const handleUpdateLiveSite = async () => {
        if (!resolvedProjectId) return;

        try {
            workflowCompletionToastShownRef.current = false;
            setShowWorkflowPanel(true);
            toast.loading("Updating live website...", { id: "hero-update-live" });

            await redeployProject(resolvedProjectId, "manual");

            toast.success("Live website update started.", { id: "hero-update-live" });
            showPageMessage("Live website update started. Track progress in the workflow panel.");

            await refreshAll();
            await pollDeploymentStatus();
        } catch (err) {
            console.error(err);
            workflowCompletionToastShownRef.current = false;
            setShowWorkflowPanel(true);
            toast.error(getErrorMessage(err, "Failed to update live website."), {
                id: "hero-update-live"
            });
        }
    };

    const handlePublishSite = async () => {
        if (!resolvedProjectId) return;

        try {
            workflowCompletionToastShownRef.current = false;
            setShowWorkflowPanel(true);
            setPublishingSite(true);

            toast.loading("Publishing website...", { id: "publish-site" });
            showPageMessage("Publishing started. Track progress in the workflow panel.");

            await publishCrmProject(resolvedProjectId);

            await refreshAll();
            await pollDeploymentStatus();

            setTimeout(loadProjectHealth, 2500);
            setTimeout(loadProjectHealth, 7000);
            setTimeout(load, 8000);

            toast.success("Publish started successfully.", { id: "publish-site" });
        } catch (err) {
            console.error(err);
            workflowCompletionToastShownRef.current = false;
            setShowWorkflowPanel(true);

            toast.error(getErrorMessage(err, "Failed to publish website."), {
                id: "publish-site"
            });

            showPageMessage("Publish failed. Review workflow and project health.", {
                sticky: true
            });
        } finally {
            setPublishingSite(false);
        }
    };

    const handleRegenerateWebsite = async () => {
        if (!resolvedProjectId) return;

        confirmWithToast({
            id: "confirm-regenerate-site",
            title: "Regenerate website?",
            message: "This will rebuild and redeploy the website using latest CRM modules.",
            confirmLabel: "Regenerate",
            onConfirm: async () => {
                try {
                    workflowCompletionToastShownRef.current = false;
                    setShowWorkflowPanel(true);
                    setRegeneratingWebsite(true);
                    toast.loading("Regenerate/redeploy queued...", { id: "regenerate-site" });

                    await redeployProject(resolvedProjectId, "full-structure-regenerate");
                    await refreshAll();
                    await pollDeploymentStatus();

                    toast.success("Website regenerate/redeploy has been queued.", { id: "regenerate-site" });
                    showPageMessage("Website regenerate/redeploy queued. Track progress in the workflow panel.");
                } catch (err) {
                    console.error(err);
                    toast.error(getErrorMessage(err, "Failed to regenerate website."), { id: "regenerate-site" });
                } finally {
                    setRegeneratingWebsite(false);
                }
            }
        });
    };

    const handleRepairWebsite = async () => {

        if (!canRunDiagnostics) {
            toast.error("You do not have permission to repair this website.");
            return;
        }

        if (!resolvedProjectId) return;

        confirmWithToast({
            id: "confirm-repair-site",
            title: "Repair website?",
            message: "This will repair and rebuild your website structure.",
            confirmLabel: "Repair",
            onConfirm: async () => {
                try {
                    workflowCompletionToastShownRef.current = false;
                    setShowWorkflowPanel(true);
                    setRepairingWebsite(true);
                    toast.loading("Website repair triggered...", { id: "repair-site" });

                    await fixWebsite(resolvedProjectId);
                    await refreshAll();
                    await pollDeploymentStatus();

                    toast.success("Website repair triggered.", { id: "repair-site" });
                    showPageMessage("Website repair triggered. Track progress in the workflow panel.");
                } catch (err) {
                    console.error(err);
                    toast.error(getErrorMessage(err, "Fix failed."), { id: "repair-site" });
                } finally {
                    setRepairingWebsite(false);
                }
            }
        });
    };

    const handleApplyVariant = async (variant) => {

        if (!canUseAiStudio) {
            toast.error("You do not have permission to apply AI section variants.");
            return;
        }

        if (!resolvedProjectId || !sectionPreview || !variant) return;

        try {
            workflowCompletionToastShownRef.current = false;
            setShowWorkflowPanel(true);
            setApplyingSectionVariant(true);
            toast.loading("Applying selected variant...", { id: "apply-section-variant" });

            await applyWebsiteSectionFix(resolvedProjectId, {
                target: sectionPreview.target,
                selectedVariant: variant.variant,
                selectedImageUrl: variant.imageUrl,
                redeploy: true
            });

            setSectionPreview(null);
            setSelectedSectionVariant(null);

            await refreshAll();
            await pollDeploymentStatus();

            toast.success(`${sectionPreview.target} variant ${variant.variant} applied and redeployed.`, {
                id: "apply-section-variant"
            });

            showPageMessage(`${sectionPreview.target} variant applied. Live website update is now in progress.`);
        } catch (err) {
            console.error(err);
            toast.error(getErrorMessage(err, "Failed to apply selected variant."), {
                id: "apply-section-variant"
            });
        } finally {
            setApplyingSectionVariant(false);
        }
    };

    const dynamicFixTargets = [
        { target: "hero", label: "Hero", icon: "✨" },
        ...(modules || []).map(m => ({
            target: resolveFixTarget(m.moduleKey),
            label: m.moduleLabel || m.moduleKey,
            icon: "✦"
        }))
    ].filter((item, index, arr) =>
        arr.findIndex(x => x.target === item.target) === index
    );

    const handleSelectiveFix = async (target) => {

        if (!canUseAiStudio) {
            toast.error("You do not have permission to generate AI section previews.");
            return;
        }

        if (!resolvedProjectId || !project) return;

        try {
            setFixingSection(target);
            setPreviewLoading(true);
            toast.loading(`Preparing ${target} variants...`, { id: "section-preview" });

            const result = await previewWebsiteSectionFix(resolvedProjectId, {
                target,
                mode: "ai-repair",
                maxVariants: 3,
                includeImageSuggestion: true,
                instruction: `Improve ${target} section for ${project.industry} / ${project.subIndustry}.`
            });

            const variants = result?.variants || [];

            /*
               Close AI Studio before opening section preview.
               This prevents two modals from stacking on top of each other.
            */
            setShowAiStudio(false);

            setSectionPreview({
                target,
                scope: result?.scope,
                variants
            });

            setSelectedSectionVariant(variants[0] || null);

            toast.success(`${target} variants ready.`, { id: "section-preview" });
        } catch (err) {
            console.error(err);
            toast.error(getErrorMessage(err, `Failed to generate ${target} previews.`), {
                id: "section-preview"
            });
        } finally {
            setPreviewLoading(false);
            setFixingSection("");
        }
    };

    if (loading) {
        return (
            <PageShell title="Project Detail">
                <div className="project-loading-card">Loading project detail...</div>
            </PageShell>
        );
    }

    if (error) {
        return (
            <PageShell title="Project Detail">
                <div className="project-loading-card">{error}</div>
            </PageShell>
        );
    }

    if (!project) {
        return (
            <PageShell title="Project Detail">
                <div className="project-loading-card">Project not found.</div>
            </PageShell>
        );
    }

    return (
        <PageShell title="Website Command Center">
            <div className="project-detail-shell">
                <section className="project-hero-card project-hero-card-v2">
                    <div className="project-hero-main">
                        <span className="project-kicker">CRM Website Command Center</span>

                        <h1>{project.businessName || "Untitled Project"}</h1>

                        <p>
                            {project.description ||
                                project.tagline ||
                                "Manage website content, CRM modules, deployment workflow, and AI repair actions from one command center."}
                        </p>

                        <div className="project-hero-actions project-hero-actions-v2">
                            <Link
                                to={`/crm/projects/${resolvedProjectId}/intelligence`}
                                className="project-action-btn primary"
                            >
                                🧠 Project Intelligence
                            </Link>

                            <Link
                                to={`/crm/projects/${resolvedProjectId}/branding`}
                                className="project-action-btn secondary"
                            >
                                🎨 Branding
                            </Link>

                            <button
                                type="button"
                                className="project-action-btn secondary"
                                onClick={() => setShowCustomDomains(true)}
                            >
                                🌐 Custom Domains
                            </button>

                            <button
                                type="button"
                                className="project-action-btn secondary"
                                onClick={() => {
                                    setShowWorkflowPanel(true);
                                    loadDeploymentStatus();
                                    toast.success("Workflow status refreshed.");

                                    setTimeout(() => {
                                        const el = document.getElementById("project-workflow-panel");
                                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                                    }, 100);
                                }}
                            >
                                Workflow
                            </button>

                            <button
                                type="button"
                                className="project-action-btn secondary"
                                onClick={() => setShowProjectDetails(true)}
                            >
                                Project Details
                            </button>

                            <button
                                type="button"
                                className="project-action-btn secondary"
                                onClick={() => {
                                    loadDeploymentVersions();
                                    setShowDeploymentRecovery(true);
                                }}
                            >
                                Deployment History
                            </button>

                            {canOpenSiteOperations && (
                                <button
                                    type="button"
                                    className="project-action-btn secondary"
                                    onClick={() => setShowSiteOperations(true)}
                                >
                                    {isSuperAdmin ? "Site Operations" : "Publish Options"}
                                </button>
                            )}

                            {canUseAiStudio && (
                                <button
                                    type="button"
                                    className="project-action-btn secondary"
                                    onClick={() => setShowAiStudio(true)}
                                >
                                    AI Studio
                                </button>
                            )}

                            {canRunDiagnostics && (
                                <button
                                    type="button"
                                    className="project-action-btn secondary"
                                    onClick={() => setShowVariantPerformance(true)}
                                >
                                    Variants
                                </button>
                            )}
                        </div>
                    </div>

                    <ProjectLiveStatusCard
                        project={project}
                        deploymentStatus={deploymentStatus}
                        health={projectHealth}
                        publishing={publishingSite}
                        updating={
                            regeneratingWebsite ||
                            repairingWebsite ||
                            repairingCrm ||
                            productionFitFixing ||
                            premiumApplying
                        }
                        onPublish={handlePublishSite}
                        onUpdate={handleUpdateLiveSite}
                        onRefresh={async () => {
                            await loadDeploymentStatus();
                            await loadProjectHealth();
                            await load();
                        }}
                    />
                </section>

                {pageMessage && (
                    <div className={`module-toast ${pageMessage.toLowerCase().includes("failed") ? "error" : "success"}`}>
                        {pageMessage}
                    </div>
                )}

                <section className="project-stats-grid">
                    {dynamicStatCards.length === 0 ? (
                        <StatCard label="Modules" value={modules.length} icon="📦" />
                    ) : (
                        dynamicStatCards.map(card => (
                            <StatCard
                                key={card.key}
                                label={card.label}
                                value={card.value}
                                icon={card.icon}
                            />
                        ))
                    )}
                </section>

                {(showWorkflowPanel ||
                    deploymentStatus?.status === "syncing" ||
                    deploymentStatus?.status === "redeploying" ||
                    deploymentStatus?.status === "synced" ||
                    deploymentStatus?.status === "failed" ||
                    deploymentStatus?.redeployStatus === "pending" ||
                    deploymentStatus?.redeployStatus === "running" ||
                    deploymentStatus?.syncStatus === "pending" ||
                    deploymentStatus?.syncStatus === "running") && (
                        <div id="project-workflow-panel">
                            <ProjectWorkflowPanel
                                projectId={resolvedProjectId}
                                deploymentStatus={deploymentStatus}
                                onRefresh={loadDeploymentStatus}
                                onOpenOperations={() => setShowOperationsModal(true)}
                            />
                        </div>
                    )}

                <ProjectHealthPanel
                    health={projectHealth}
                    onRefresh={loadProjectHealth}
                    onOpenSeedPicker={() => {
                        if (!isSuperAdmin) return;
                        setShowSeedModal(true);
                    }}
                    onRepairCrm={handleRepairCrmFromSeed}
                    onRedeploy={handleUpdateLiveSite}
                    repairingCrm={repairingCrm}
                    redeploying={
                        healthLoading ||
                        publishingSite ||
                        regeneratingWebsite ||
                        repairingWebsite ||
                        repairingCrm ||
                        productionFitFixing ||
                        premiumApplying
                    }
                    isSuperAdmin={isSuperAdmin}
                />

                <ProjectLeadSummaryPanel
                    summary={projectLeadSummary}
                    loading={leadSummaryLoading}
                    onRefresh={loadProjectLeadSummary}
                />

                {canAssignAccounts && (
                    <ProjectAccountAssignCard
                        project={project}
                        accounts={accounts}
                        onAssigned={load}
                    />
                )}

                <section className="project-main-grid">
                    <div className="project-left-column">
                        <div className="project-panel">
                            <div className="project-panel-header">
                                <div>
                                    <span className="project-kicker">Content Modules</span>
                                    <h2>Editable Website Data</h2>
                                </div>
                            </div>

                            {modules.length === 0 ? (
                                <div className="project-empty">
                                    Modules are being prepared. Try repairing or regenerating CRM from seed.
                                </div>
                            ) : (
                                <div className="module-premium-grid">
                                    {modules.map(m => (
                                        <Link
                                            key={m.moduleKey}
                                            className="module-premium-card"
                                            to={`/crm/projects/${resolvedProjectId}/modules/${m.moduleKey}`}
                                        >
                                            <span>{m.moduleType || "module"}</span>
                                            <strong>{m.moduleLabel}</strong>
                                            <small>Edit records →</small>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </section>
                {canRunDiagnostics && (
                    <section className="project-panel production-fit-panel">
                        <div className="production-fit-head">
                            <div>
                                <span className="project-kicker">Nexus Production Fit</span>
                                <h2>Website Quality Check</h2>
                                <p>
                                    Check whether this website looks polished, industry-specific, mobile-ready,
                                    and safe to show to a real client.
                                </p>
                            </div>

                            <button
                                type="button"
                                className="project-action-btn primary"
                                onClick={handleAnalyzeProductionFit}
                                disabled={productionFitAnalyzing}
                            >
                                {productionFitAnalyzing ? "Checking..." : "Run Quality Check"}
                            </button>

                            <button
                                type="button"
                                className="project-action-btn secondary"
                                onClick={handleFixProductionIssues}
                                disabled={
                                    productionFitFixing ||
                                    !productionFit ||
                                    productionFit.status === "NotReviewed"
                                }
                            >
                                {productionFitFixing ? "Applying..." : "Apply Quality Fixes"}
                            </button>
                        </div>

                        {productionFitLoading ? (
                            <div className="project-loading-card">Loading production fit...</div>
                        ) : (
                            <>
                                <div className="production-fit-score-row">
                                    <div className="production-fit-score">
                                        <strong>{productionFit?.productionReadyScore ?? 0}</strong>
                                        <span>{productionFit?.status || "Not Reviewed"}</span>
                                    </div>

                                    <div className="production-fit-metrics">
                                        <div>
                                            <span>Visual</span>
                                            <strong>{productionFit?.visualScore ?? "-"}</strong>
                                        </div>
                                        <div>
                                            <span>Content</span>
                                            <strong>{productionFit?.contentSpecificityScore ?? "-"}</strong>
                                        </div>
                                        <div>
                                            <span>Conversion</span>
                                            <strong>{productionFit?.conversionScore ?? "-"}</strong>
                                        </div>
                                        <div>
                                            <span>Mobile</span>
                                            <strong>{productionFit?.mobileScore ?? "-"}</strong>
                                        </div>
                                    </div>
                                </div>

                                <p className="production-fit-summary">
                                    {productionFit?.summary || "Run a quality check to get a clear score and improvement suggestions before sharing this site."}
                                </p>

                                {productionFit?.recommendedThemeKey && (
                                    <div className="production-fit-recommendation">
                                        <span>Recommended Theme</span>
                                        <strong>{productionFit.recommendedThemeKey}</strong>
                                    </div>
                                )}

                                {productionFit?.upgradeStrategy && (
                                    <div className="production-fit-upgrade">
                                        <span>Upgrade Strategy</span>
                                        <p>{productionFit.upgradeStrategy}</p>
                                    </div>
                                )}

                                {productionFit?.issues?.length > 0 && (
                                    <div className="production-fit-issues">
                                        <h3>Issues Found</h3>

                                        {productionFit.issues.slice(0, 5).map((issue, index) => (
                                            <div key={`${issue.area}-${index}`} className="production-fit-issue">
                                                <div>
                                                    <strong>{issue.area || "General"}</strong>
                                                    <span className={`production-fit-severity ${String(issue.severity || "medium").toLowerCase()}`}>
                                                        {issue.severity || "medium"}
                                                    </span>
                                                </div>

                                                <p>{issue.reason}</p>
                                                <small>{issue.suggestedFix}</small>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </section>

                )}
            </div>

            {isSuperAdmin && showSeedModal && (
                <SeedPickerModal
                    projectId={resolvedProjectId}
                    onClose={() => setShowSeedModal(false)}
                    onSuccess={refreshAll}
                />
            )}

            <SectionPreviewModal
                project={project}
                preview={sectionPreview}
                selectedVariant={selectedSectionVariant}
                onSelect={setSelectedSectionVariant}
                onApply={handleApplyVariant}
                onClose={() => {
                    setSectionPreview(null);
                    setSelectedSectionVariant(null);
                }}
                applying={applyingSectionVariant}
            />

            {showProjectDetails && (
                <ProjectDetailsModal
                    project={project}
                    resolvedProjectId={resolvedProjectId}
                    onClose={() => setShowProjectDetails(false)}
                />
            )}

            {showCustomDomains && (
                <div className="project-modal-backdrop">
                    <div className="project-modal project-domains-modal">
                        <div className="project-modal-header">
                            <div>
                                <span className="project-kicker">Custom Domains</span>
                                <h3>Connect client domain</h3>
                                <p>
                                    Add a custom domain, copy DNS records, verify ownership,
                                    and set the primary live domain for this project.
                                </p>
                            </div>

                            <button
                                type="button"
                                className="project-modal-close"
                                onClick={() => setShowCustomDomains(false)}
                                aria-label="Close custom domains"
                            >
                                ×
                            </button>
                        </div>

                        <div className="project-modal-scroll">
                            <ProjectDomainsPanel
                                projectId={resolvedProjectId}
                                liveUrl={project?.liveUrl}
                                onProjectChanged={load}
                                embedded
                            />
                        </div>
                    </div>
                </div>
            )}

            {showDeploymentRecovery && (
                <div className="project-modal-backdrop">
                    <div className="project-modal deployment-recovery-modal">
                        <div className="project-modal-header">
                            <div>
                                <span className="project-kicker">Recovery</span>
                                <h3>Deployment History</h3>
                                <p>
                                    Restore a previous live website version without changing CRM records or module content.
                                </p>
                            </div>

                            <button type="button" onClick={() => setShowDeploymentRecovery(false)}>
                                ×
                            </button>
                        </div>

                        {loadingVersions ? (
                            <div className="project-loading-card">Loading deployment history...</div>
                        ) : deploymentVersions.length === 0 ? (
                            <div className="project-empty">
                                No deployment versions found yet. Deploy the site once with versioned deployments enabled.
                            </div>
                        ) : (
                            <>
                                <div className="recovery-summary-grid recovery-summary-grid-modal">
                                    {(() => {
                                        const safeVersions = Array.isArray(deploymentVersions) ? deploymentVersions : [];
                                        const activeVersion = safeVersions.find(v => v.isActive) || safeVersions[0] || null;
                                        const previousVersions = safeVersions.filter(v => !v.isActive);
                                        const latestPrevious = previousVersions[0] || null;

                                        return (
                                            <>
                                                <div className="recovery-summary-card active">
                                                    <span>Current Live Version</span>
                                                    <strong>{activeVersion?.version || "-"}</strong>
                                                    <small>
                                                        {activeVersion?.createdAtUtc
                                                            ? new Date(activeVersion.createdAtUtc).toLocaleString()
                                                            : "Currently active deployment"}
                                                    </small>
                                                </div>

                                                <div className="recovery-summary-card">
                                                    <span>Available Restore Points</span>
                                                    <strong>{previousVersions.length}</strong>
                                                    <small>
                                                        {previousVersions.length > 0
                                                            ? "Previous versions are available for recovery."
                                                            : "No previous restore point yet."}
                                                    </small>
                                                </div>

                                                <div className="recovery-summary-card recovery-quick-restore">
                                                    <span>Latest Previous Version</span>
                                                    <strong>{latestPrevious?.version || "-"}</strong>
                                                    <small>
                                                        {latestPrevious?.createdAtUtc
                                                            ? new Date(latestPrevious.createdAtUtc).toLocaleString()
                                                            : "No previous version available."}
                                                    </small>

                                                    {latestPrevious && (
                                                        <button
                                                            type="button"
                                                            className="project-action-btn secondary"
                                                            disabled={restoringVersion === latestPrevious.version}
                                                            onClick={() => handleRollbackVersion(latestPrevious.version)}
                                                        >
                                                            {restoringVersion === latestPrevious.version
                                                                ? "Restoring..."
                                                                : "Quick Restore"}
                                                        </button>
                                                    )}
                                                </div>
                                            </>
                                        );
                                    })()}
                                </div>

                                <div className="deployment-history-toolbar deployment-history-toolbar-inline">
                                    <span>
                                        {deploymentVersions.length} deployment version{deploymentVersions.length === 1 ? "" : "s"}
                                    </span>

                                    <button
                                        type="button"
                                        className="project-action-btn secondary"
                                        onClick={loadDeploymentVersions}
                                        disabled={loadingVersions}
                                    >
                                        Refresh History
                                    </button>
                                </div>

                                <div className="deployment-version-list modal-version-list deployment-version-list-visible">
                                    {deploymentVersions.map(v => (
                                        <div
                                            key={v.version}
                                            className={`deployment-version-row ${v.isActive ? "active" : ""}`}
                                        >
                                            <div>
                                                <strong>{v.version}</strong>
                                                <span>
                                                    {v.isActive
                                                        ? "Current live version"
                                                        : v.createdAtUtc
                                                            ? new Date(v.createdAtUtc).toLocaleString()
                                                            : "Previous version"}
                                                </span>
                                            </div>

                                            {v.isActive ? (
                                                <span className="deploy-status-pill live">Active</span>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="project-action-btn secondary"
                                                    disabled={restoringVersion === v.version}
                                                    onClick={() => handleRollbackVersion(v.version)}
                                                >
                                                    {restoringVersion === v.version ? "Restoring..." : "Restore"}
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {showSiteOperations && (
                <div className="project-modal-backdrop">
                    <div className="project-modal site-operations-modal">
                        <div className="project-modal-header">
                            <div>
                                <span className="project-kicker">
                                    {isSuperAdmin ? "Site Operations" : "Publish Options"}
                                </span>
                                <h3>{isSuperAdmin ? "Website Actions" : "Publish Website"}</h3>
                                <p>
                                    {isSuperAdmin
                                        ? "Use these actions only when you need to publish, rebuild, repair, or reset CRM website content."
                                        : "Publish the reviewed generated website when it is ready to go live."}
                                </p>
                            </div>

                            <button type="button" onClick={() => setShowSiteOperations(false)}>
                                ×
                            </button>
                        </div>

                        <div className="site-operations-modal-grid">
                            {project.status === "Generated" && (!project.isPublished || !project.liveUrl) ? (
                                <div className="operation-action-card recommended">
                                    <div>
                                        <strong>Publish Website</strong>
                                        <OperationNote>
                                            Makes this generated website live for the first time after reviewing the content.
                                        </OperationNote>
                                    </div>

                                    <ActionButton
                                        variant="primary"
                                        disabled={publishingSite}
                                        onClick={handlePublishSite}
                                    >
                                        {publishingSite ? "Publishing..." : "Publish"}
                                    </ActionButton>
                                </div>
                            ) : null}

                            {isSuperAdmin && (
                                <>
                                    <div className="operation-action-card">
                                        <div>
                                            <strong>Rebuild Website Layout</strong>
                                            <OperationNote>
                                                Super admin only. Rebuilds the website layout using latest CRM modules. Use this when pages or navigation look wrong.
                                            </OperationNote>
                                        </div>

                                        <ActionButton
                                            variant="primary"
                                            disabled={regeneratingWebsite}
                                            onClick={handleRegenerateWebsite}
                                        >
                                            {regeneratingWebsite ? "Rebuilding..." : "Rebuild"}
                                        </ActionButton>
                                    </div>

                                    <div className="operation-action-card danger-soft">
                                        <div>
                                            <strong>Repair Website with AI</strong>
                                            <OperationNote>
                                                Super admin only. Lets AI repair broken structure, missing sections, or visual issues, then queues an update.
                                            </OperationNote>
                                        </div>

                                        <ActionButton
                                            variant="danger"
                                            disabled={repairingWebsite}
                                            onClick={handleRepairWebsite}
                                        >
                                            {repairingWebsite ? "Repairing..." : "Repair"}
                                        </ActionButton>
                                    </div>

                                    <div className="operation-action-card">
                                        <div>
                                            <strong>Reset CRM Content from Seed</strong>
                                            <OperationNote>
                                                Super admin only. Rebuilds CRM modules from approved seed data for this vertical.
                                            </OperationNote>
                                        </div>

                                        <ActionButton
                                            onClick={() => {
                                                setShowSiteOperations(false);
                                                setShowSeedModal(true);
                                            }}
                                        >
                                            Select Seed
                                        </ActionButton>
                                    </div>
                                </>
                            )}

                            {!isSuperAdmin &&
                                !(project.status === "Generated" && (!project.isPublished || !project.liveUrl)) && (
                                    <div className="project-empty">
                                        No publish action is currently required. You can update the live website from the main project header after editing CRM content.
                                    </div>
                                )}
                        </div>
                    </div>
                </div>
            )}

            {isSuperAdmin && showAiStudio && (
                <div className="project-modal-backdrop">
                    <div className="project-modal ai-studio-modal">
                        <div className="project-modal-header">
                            <div>
                                <span className="project-kicker">Nexus AI Studio</span>
                                <h3>Premium Improvements</h3>
                                <p>
                                    Generate premium improvements or repair one specific website section without resetting the whole project.
                                </p>
                            </div>

                            <button type="button" onClick={() => setShowAiStudio(false)}>
                                ×
                            </button>
                        </div>

                        <div className="ai-studio-grid">
                            <section className="ai-studio-card ai-studio-card-main">
                                <div className="ai-studio-card-head">
                                    <div>
                                        <span className="project-kicker">Premium Upgrade</span>
                                        <h3>Generate Premium Preview</h3>
                                        <p>
                                            Create AI-selected premium section upgrades before applying them to the live project.
                                        </p>
                                    </div>

                                    <div className="ai-studio-actions">
                                        <button
                                            type="button"
                                            className="project-action-btn secondary"
                                            onClick={handleGeneratePremiumPreview}
                                            disabled={premiumGenerating}
                                        >
                                            {premiumGenerating ? "Preparing..." : "Preview"}
                                        </button>

                                        <button
                                            type="button"
                                            className="project-action-btn primary"
                                            onClick={handleApplyPremiumUpgrade}
                                            disabled={
                                                premiumApplying ||
                                                !premiumUpgrade?.targets ||
                                                premiumUpgrade.targets.length === 0
                                            }
                                        >
                                            {premiumApplying ? "Applying..." : "Apply"}
                                        </button>
                                    </div>
                                </div>

                                {premiumLoading ? (
                                    <div className="project-loading-card">Loading premium upgrade...</div>
                                ) : (
                                    <>
                                        <div className="premium-upgrade-status-box">
                                            <p className="premium-upgrade-summary">
                                                {premiumUpgrade?.summary ||
                                                    "No premium preview generated yet. Click Preview to generate recommended improvements for this project."}
                                            </p>

                                            <div className="premium-upgrade-dates">
                                                {premiumUpgrade?.previewCreatedAtUtc && (
                                                    <span>
                                                        Preview generated:{" "}
                                                        <strong>{new Date(premiumUpgrade.previewCreatedAtUtc).toLocaleString()}</strong>
                                                    </span>
                                                )}

                                                {premiumUpgrade?.appliedAtUtc && (
                                                    <span>
                                                        Last applied:{" "}
                                                        <strong>{new Date(premiumUpgrade.appliedAtUtc).toLocaleString()}</strong>
                                                    </span>
                                                )}

                                                {premiumUpgrade?.status && (
                                                    <span>
                                                        Status: <strong>{premiumUpgrade.status}</strong>
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {premiumUpgrade?.targets?.length > 0 ? (
                                            <div className="premium-upgrade-targets compact premium-upgrade-targets-visible">
                                                {premiumUpgrade.targets.map((target) => {
                                                    const variants = target?.preview?.variants || [];

                                                    return (
                                                        <div key={target.target} className="premium-upgrade-target premium-upgrade-target-visible">
                                                            <div>
                                                                <strong>{target.label || target.target}</strong>
                                                                <span>{target.target}</span>
                                                            </div>

                                                            <p>{target.reason}</p>

                                                            <small>
                                                                {variants.length > 0
                                                                    ? `${variants.length} preview variants ready · Recommended: ${target.recommendedVariant || 1}`
                                                                    : `Recommended variant: ${target.recommendedVariant || 1}`}
                                                            </small>

                                                            <button
                                                                type="button"
                                                                className="project-action-btn secondary premium-target-preview-btn"
                                                                onClick={() => handleOpenPremiumTargetPreview(target)}
                                                                disabled={variants.length === 0}
                                                            >
                                                                Preview Variants
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="project-empty">
                                                {premiumUpgrade
                                                    ? "Premium preview was generated, but no section targets were returned by the API."
                                                    : "Generate a premium preview to see recommended improvements."}
                                            </div>
                                        )}
                                    </>
                                )}
                            </section>

                            <section className="ai-studio-card">
                                <div className="ai-studio-card-head stacked">
                                    <div>
                                        <span className="project-kicker">Selective Fix</span>
                                        <h3>Improve One Section</h3>
                                        <p>
                                            Repair one section using AI variants, then preview and apply only the selected result.
                                        </p>
                                    </div>
                                </div>

                                <div className="ai-fix-grid ai-fix-grid-modal">
                                    {dynamicFixTargets.map(item => (
                                        <ActionButton
                                            key={item.target}
                                            disabled={previewLoading || !!fixingSection}
                                            onClick={() => handleSelectiveFix(item.target)}
                                        >
                                            {fixingSection === item.target
                                                ? "Preparing..."
                                                : `${item.icon} ${item.label}`}
                                        </ActionButton>
                                    ))}
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            )}

            {showVariantPerformance && (
                <div className="project-modal-backdrop">
                    <div className="project-modal variant-performance-modal">
                        <div className="project-modal-header">
                            <div>
                                <span className="project-kicker">A/B Intelligence</span>
                                <h3>Variant Performance</h3>
                                <p>
                                    Compare current variants, preview experiences, and evolve a stronger challenger.
                                </p>
                            </div>

                            <button type="button" onClick={() => setShowVariantPerformance(false)}>
                                ×
                            </button>
                        </div>

                        {!variantStats ? (
                            <div className="project-empty">No variant data yet.</div>
                        ) : (
                            <>
                                <div className="variant-performance-stats">
                                    <InfoPill label="Active" value={variantStats.activeVariant || "A"} />
                                    <InfoPill label="Winner" value={variantStats.winningVariantKey || "Learning"} />
                                </div>

                                <div className="variant-performance-actions">
                                    <a
                                        className="project-action-btn secondary"
                                        href={`https://api.shrianshnexus.in/api/website/${resolvedProjectId}/preview-document?v=A`}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        View Variant A
                                    </a>

                                    <a
                                        className="project-action-btn secondary"
                                        href={`https://api.shrianshnexus.in/api/website/${resolvedProjectId}/preview-document?v=B`}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        View Variant B
                                    </a>

                                    <ActionButton
                                        disabled={variantBusy}
                                        onClick={async () => {
                                            setVariantBusy(true);
                                            try {
                                                toast.loading("Regenerating AI challenger...", { id: "variant-challenger" });
                                                await regenerateChallenger(resolvedProjectId);
                                                await loadVariantStats();
                                                toast.success("AI challenger regenerated.", { id: "variant-challenger" });
                                            } catch (err) {
                                                console.error(err);
                                                toast.error(getErrorMessage(err, "Failed to regenerate challenger."), {
                                                    id: "variant-challenger"
                                                });
                                            } finally {
                                                setVariantBusy(false);
                                            }
                                        }}
                                    >
                                        Regenerate Challenger
                                    </ActionButton>

                                    <ActionButton
                                        variant="danger"
                                        disabled={variantBusy}
                                        onClick={() => {
                                            confirmWithToast({
                                                id: "confirm-reset-variant-stats",
                                                title: "Reset variant stats?",
                                                message: "This will clear A/B performance data for this project. Website content and CRM data will not be changed.",
                                                confirmLabel: "Reset Stats",
                                                onConfirm: async () => {
                                                    setVariantBusy(true);

                                                    try {
                                                        toast.loading("Resetting variant stats...", { id: "variant-reset" });

                                                        await resetVariantStats(resolvedProjectId);
                                                        await loadVariantStats();

                                                        toast.success("Variant stats reset.", { id: "variant-reset" });
                                                    } catch (err) {
                                                        console.error(err);
                                                        toast.error(getErrorMessage(err, "Failed to reset variant stats."), {
                                                            id: "variant-reset"
                                                        });
                                                    } finally {
                                                        setVariantBusy(false);
                                                    }
                                                }
                                            });
                                        }}
                                    >
                                        Reset Stats
                                    </ActionButton>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {
                showOperationsModal && (
                    <ProjectOperationsModal
                        projectId={resolvedProjectId}
                        onClose={() =>
                            setShowOperationsModal(false)
                        }
                        onChanged={refreshAll}
                    />
                )
            }

        </PageShell>
    );
}